<template>
  <div class="bg-[#202020]" @drop="onDropFileIntoEditor" @dragover.prevent>
    <EditorTopTools />
    <div class="flex w-full h-[calc(100vh-100px)]">
      <!-- 左侧面板 -->
      <div
        ref="leftPanel"
        class="w-86 px-3 flex-shrink-0 overflow-overlay relative"
      >
        <EditorResourceList />
        <ColumnResizer
          class="right-0 top-0"
          :div-el="leftPanel"
          :min="250"
          :max="500"
        />
      </div>

      <!-- 中间区域 -->
      <div
        class="w-full relative flex-1 flex overflow-hidden flex-shrink-0 flex-col z-0"
      >
        <div class="flex-shrink-0 bg-[#313131] relative h-10 rounded-lg mb-2.5">
          <editor-color-tabs />
        </div>
        <div class="flex-shrink-0 overflow-hidden flex-1 flex transition-all">
          <!-- canvas render -->
          <div
            class="overflow-hidden w-full h-full rounded-lg relative"
            :style="{
              backgroundImage: `linear-gradient(${editorStore.canvasBgColor},${editorStore.canvasBgColor}), linear-gradient(45deg, #ccc 25%, transparent 0, transparent 75%, #ccc 0 ), linear-gradient(45deg, #ccc 25%, #fff 0, #fff 75%, #ccc 0)`,
              backgroundPosition: '0 0, 0 0, 10px 10px',
              backgroundSize: '20px 20px'
            }"
          >
            <div
              id="workbench-outer"
              class="overflow-overlay w-full h-full relative scroll-bar"
            >
              <EditorWatchCanvas />
            </div>
            <!-- 预览区打开/关闭按钮 -->
            <div
              class="absolute right-0 top-0 w-10 h-24 bg-[#202020] rounded-lg rounded-r-none flex justify-center py-2.5 flex-col items-center transition-all"
              :class="{
                ' !w-0 opacity-0': expand
              }"
              @click.stop="onExpand"
            >
              <span
                class="text-sm mb-1 tracking-[3.2px]"
                :style="{
                  writingMode: 'vertical-lr'
                }"
                >预览区</span
              >
              <icon
                :icon="'expand1.svg'"
                :widht="20"
                :height="20"
                fill="none"
              />
            </div>
          </div>

          <!-- 预览区 -->
          <div
            class="flex-shrink-0 bg-[#202020] overflow-hidden transition-all"
            :class="{
              ' w-[18.75rem]': expand,
              ' w-0': !expand
            }"
          >
            <editor-watchface-preview class="" />
          </div>
        </div>
        <!-- 底部状态栏 -->
        <div class="flex-shrink-0">
          <editor-status-bar />
        </div>
      </div>
      <!-- 右侧面板 -->
      <div
        :key="snapshotKey"
        ref="rightPanel"
        class="w-[22rem] px-3 flex-shrink-0 flex flex-col"
      >
        <!-- tabs -->
        <div
          class="flex items-center h-10 bg-black-card justify-center rounded-lg"
        >
          <div
            v-for="tab in tabs"
            :key="tab.title"
            class="flex-1 w-full flex items-center justify-center h-full rounded-lg"
            :class="{
              'text-xs bg-black-active': activeKey == tab.key,
              '!opacity-40 cursor-not-allowed': tab.disabeld
            }"
            @click="
              () => {
                if (!tab.disabeld) activeKey = tab.key;
              }
            "
          >
            {{ tab.title }}
          </div>
        </div>
        <div class="overflow-auto relative h-full flex-1 flex-shrink-0">
          <!-- 属性面板 -->
          <EditorResourceAttr v-if="activeKey == 'attr'" />
          <!-- 像素校验 -->
          <editor-watchface-aod-valid v-if="activeKey == 'valid'" />
          <!-- 显示配置面板(没有选择图层时) -->
          <editor-theme-config v-if="activeKey == 'config'" />
          <!-- 全局蒙层（AOD下） -->
          <editor-global-mask v-if="activeKey == 'config'" />
          <!-- 显示全局缩放（没有选择图层时，绑定在主题下） -->
          <editor-global-scale-config v-if="activeKey == 'config'" />
  
          <!-- animation -->
          <editor-theme-animation v-if="activeKey == 'animation'" />

          <!-- 容器跟随开关 -->
          <AttrSwitch
            v-if="
              activeKey == 'config' && canUse('support-slot-following').valid
            "
            class="mt-4"
            label="容器跟随"
            :value="watchStore.isSlotFollowing"
            @change="(val: boolean) => editSlotFollowing(val)"
          />
          
          <popup-bgcolor-picker />
          <!-- 固件端预览教学视频浮层 -->
          <popup-send-tutorial />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useDataSource,
  useEditorDrag,
  useEditorInit,
  useEditorSnapshot,
  useGlobalShortcuts,
  useWatchfaceStatus,
  useWebVersion
} from '@/hooks';
import { useEditorPopups } from '@/hooks/use-editor-popups';
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { emitter, EventType } from '@/utils/emitter.utils';
import { cloneDeep } from 'lodash-es';
import { onActivated, onDeactivated, onUnmounted, watch } from 'vue';
import { computed } from 'vue';
import { ref } from 'vue';
const editorStore = useEditorStore();
const watchStore = useWatchfaceStore();
const layerStore = useLayerStore();
const { deleteFollowingSlotAttr } = useSlotFollow();

// const { canuseAODValid } = useWatchfaceAodPixel();
// const layerStore = useLayerStore();
// const isAOD = computed(() => editorStore.theme?.type == 'AOD');
// const showColorTab = computed(() => {
//   // 单一息屏时=禁用多配色
//   if (isAOD.value && layerStore.isSingleAOD) return false;
//   return true;
// });
// 初始化表盘数据
useEditorInit();
useWatchfaceStatus();

// 拖拽图片
const { onDropFileIntoEditor } = useEditorDrag();

// 撤销恢复
const { snapshotKey } = useEditorSnapshot();
watch(snapshotKey, () => {
  // 修改当前active的shape属性
  editorStore.activeShapes.forEach((shape) => {
    const shapeId = shape?.id;
    const shapeNew = Shape.getShape(shapeId ?? '');
    if (shape && shapeNew) shape.attrs = cloneDeep(shapeNew?.attrs ?? {});
  });
});

// 左右panel
const leftPanel = ref<HTMLDivElement>();
const rightPanel = ref<HTMLDivElement>();

// 右侧面板显示tab
interface TabItem {
  title: string;
  key: string;
  disabeld?: boolean;
}
const tabs = computed(() => {
  const items: TabItem[] = [];
  // 当选择图层时，显示属性和动效面板
  if (editorStore.selectedIds.length) {
    items.push({ title: '属性', key: 'attr' });
    const isAnimation =
      canUse('animation').valid && editorStore.theme?.type == 'normal';
    const isAodAnimation =
      canUse('animation-in-AOD').valid && editorStore.theme?.type == 'AOD';
    if (isAnimation || isAodAnimation) {
      items.push({ title: '动效', key: 'animation' });
    }
  } else {
    // 没有选择图层时，显示配置面板
    items.push({ title: '配置', key: 'config' });

    // 息屏校验规则
    // 暂时去掉
    // if (editorStore.theme?.type == 'AOD' && canuseAODValid.value) {
    //   items.push({
    //     title: '息屏校验',
    //     key: 'valid'
    //   });
    // }
  }

  return items;
});
const activeKey = ref('config');
watch(
  () => editorStore.selectedIds,
  (ids) => {
    if (!ids.length) {
      activeKey.value = 'config';
    } else {
      activeKey.value = activeKey.value != 'config' ? activeKey.value : 'attr';
    }
  }
);

const { autoCheckVersion } = useWebVersion();

onActivated(() => {
  autoCheckVersion();
});

// 预览区展开折叠
const { expand } = useDataSource();
const onExpand = () => {
  expand.value = !expand.value;
};

useGlobalShortcuts(
  [
    {
      keyboardKeys: ['CommandOrControl', 'A'],
      handler: () => {
        editorStore.select(
          Shape.shapeTree(editorStore.themeId, false)
            .filter((shape) => shape.type !== ShapeType.PhotoAlbumBg)
            .map((shape) => shape.id)
        );
      }
    }
  ],
  () => {
    return editorStore.activeShapes.some(
      (shape) => shape && shape.type !== ShapeType.PhotoAlbumBg
    );
  }
);

const editSlotFollowing = (val: boolean) => {
  watchStore.slotFollowing = val;
  if (!val) {
    const firstThemeSlots =
      layerStore.themes[0].children.filter(
        (item) => item.type === ShapeType.Slot
      ) || [];
    firstThemeSlots.forEach((slot) => {
      const slotShape = Shape.getShape(slot.id);
      if (slotShape?.attrs.followingGroupName) {
        slotShape?.setAttrs({
          followingGroupName: undefined
        });
        delete slot.attrs.followingGroupName;
        deleteFollowingSlotAttr(slot.id);
      }
    });
  }
  emitter.emit(EventType['theme:set-attr']);
};

const { clear: clearPopups } = useEditorPopups();
onUnmounted(() => {
  clearPopups();
});
onDeactivated(() => {
  clearPopups();
});
</script>

<style lang="less" scoped></style>
